/**
 * useGlobalTracking — captures phone, email, and scroll events site-wide.
 * Mount once in App.js. Uses event delegation so no per-link handlers needed.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import analytics from "@/utils/analytics";

export default function useGlobalTracking() {
  const firedDepths = useRef(new Set());
  const { pathname } = useLocation();

  useEffect(() => {
    // ─── CLICK DELEGATION: shared, semantic navigation only ───
    // Never infer a label from DOM text. Page-specific CTAs and guide links
    // own their explicit contracts; this captures common nav/footer actions.
    function handleClick(e) {
      const link = e.target.closest("a[href]");
      if (!link) return;
      if (link.closest("[data-measurement-owned]")) return;
      const href = link.getAttribute("href") || "";
      const componentId = link.closest("nav")
        ? "global_navigation"
        : link.closest("footer")
          ? "global_footer"
          : link.closest('[data-testid="sticky-quote-bar"]')
            ? "sticky_quote_bar"
            : "page_content";

      if (href.startsWith("tel:")) {
        analytics.clickPhone(undefined, componentId || "sitewide_contact");
      } else if (href.startsWith("mailto:")) {
        analytics.clickEmail(undefined, componentId || "sitewide_contact");
      } else if (/^(https?:)?\/\/(wa\.me|api\.whatsapp\.com)/i.test(href)) {
        // Existing WhatsApp controls emit click_whatsapp with their approved
        // placement. Do not create a second generic navigation event.
        return;
      } else if (componentId) {
        if (href.startsWith("#")) {
          analytics.clickNavigation(componentId, href, "anchor");
          return;
        }
        try {
          const baseHref = typeof window.location.href === "string" && window.location.href
            ? window.location.href
            : window.location.origin;
          const destination = new URL(href, baseHref);
          const sameOrigin = destination.origin === window.location.origin;
          const isDownload = link.hasAttribute("download") || /\.(pdf|docx?|xlsx?|csv)$/i.test(destination.pathname);
          analytics.clickNavigation(
            componentId,
            href,
            isDownload ? "download" : sameOrigin ? "internal_navigation" : "outbound",
          );
        } catch (_err) {
          // Unparseable destinations are intentionally excluded.
        }
      }
    }

    // ─── SCROLL DEPTH: 25%, 50%, 75%, 90% ────────────────────
    const depths = firedDepths;
    function handleScroll() {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round((scrollTop / docHeight) * 100);

      for (const threshold of [25, 50, 75, 90]) {
        if (pct >= threshold && !depths.current.has(threshold)) {
          if (analytics.scrollDepth(threshold)) depths.current.add(threshold);
        }
      }
    }

    document.addEventListener("click", handleClick);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("click", handleClick);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Reset only after an actual SPA route navigation. A denied scroll
  // does not consume a threshold, so the first eligible scroll can record it.
  useEffect(() => {
    firedDepths.current.clear();
  }, [pathname]);
}
